// src/lib/use-resas-v2.js — WAGON M (Lot M3.2)
//
// Hook React I/O qui charge les rows `reservations` (V2 relationnel Supabase)
// des logements basculés en source 'v2', pour alimenter la PROJECTION de
// facturation (`projectDataForFacturation`, src/lib/data-projection.js — PUR).
//
// Calque de use-pricing-blocks-history.js (même pattern lifting App.jsx :
// 1 fetch global au mount + invalidate() exposé pour refresh post-mutation).
//
// Indexation : Map<logement_id, Array<row>>. Le module pur consommateur
// (getResasForMonth) impute chaque row à son mois — le hook ne fait AUCUN calcul
// métier, juste l'I/O + le regroupement par logement_id.
//
// ⚠️ COURT-CIRCUIT : `enabled = v2Lids.length > 0` (calculé côté appelant).
// En prod actuelle AUCUN logement n'est basculé → enabled=false → 0 fetch,
// 0 row, Map vide → la projection court-circuite (data identique).
//
// ⚠️ RGPD : guest_first_name / guest_last_name sont chargés (nécessaires pour la
// colonne « Voyageur » des factures proprio) — OK en mémoire admin, JAMAIS
// dans les logs. On ne logge ici que { data, error } et des compteurs, jamais
// le contenu PII des rows.
//
// RLS : la policy admin_select sur `reservations` couvre cette lecture (même
// pattern que CalendarPanel). Le rôle menage n'appelle pas ce hook (enabled
// false côté App via le gating ready + admin).

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabase'

const EMPTY_MAP = new Map()

// Colonnes strictement nécessaires à la projection + au snapshot facture.
// (Pas de SELECT * : on limite la surface PII et le poids réseau.)
// ⚠️ Chaque nom DOIT exister dans le schéma réel de `reservations` — une
// colonne fantôme fait échouer TOUT le SELECT (400 PostgREST) et VIDE la
// projection (bug réel attrapé en vérification Playwright prod 13/06 :
// `external_id` n'existe pas ici ; l'identifiant stable = beds24_booking_id
// + raw_payload.apiReference). Test de garde : use-resas-v2.test.js compare
// cette liste aux colonnes réelles via les fixtures du schéma.
const SELECT_COLS = [
  'id',
  'beds24_booking_id',
  'source',
  'status',
  'logement_id',
  'start_date',
  'end_date',
  'total_amount',
  'cleaning_fee',
  'city_tax',
  'ota_commission',
  'guest_first_name',
  'guest_last_name',
  'raw_payload',
].join(', ')

/**
 * Charge les rows reservations V2 des logements 'v2' + indexe par logement_id.
 *
 * @param {boolean} enabled — fetch uniquement si true (appelant : v2Lids.length>0 && admin).
 * @param {string[]} v2Lids — logement_id à charger (logs feuilles + chambres sous-loc).
 * @returns {{ resasByLogement: Map, loading: boolean, error: string|null, invalidate: () => void }}
 */
export function useResasV2(enabled = false, v2Lids = []) {
  const [state, setState] = useState({
    resasByLogement: EMPTY_MAP,
    loading: false,
    error: null,
  })
  const [refetchTick, setRefetchTick] = useState(0)

  // Clé stable de la liste de lids (évite un refetch si même contenu, ordre
  // différent). Tri + join : suffisant pour la volumétrie (qq logements).
  const lidsKey = Array.isArray(v2Lids) ? [...v2Lids].sort().join(',') : ''

  useEffect(() => {
    if (!enabled || !lidsKey) {
      setState({ resasByLogement: EMPTY_MAP, loading: false, error: null })
      return
    }
    const lids = lidsKey.split(',').filter(Boolean)
    if (lids.length === 0) {
      setState({ resasByLogement: EMPTY_MAP, loading: false, error: null })
      return
    }

    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))

    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('reservations')
          .select(SELECT_COLS)
          .in('logement_id', lids)

        if (cancelled) return
        if (error) {
          // RGPD : on logge le message d'erreur Supabase, jamais les rows.
          console.error('[useResasV2] supabase error:', error)
          setState({
            resasByLogement: EMPTY_MAP,
            loading: false,
            error: error.message,
          })
          return
        }

        const map = new Map()
        for (const row of data || []) {
          const lid = row.logement_id
          if (!lid) continue
          if (!map.has(lid)) map.set(lid, [])
          map.get(lid).push(row)
        }
        setState({ resasByLogement: map, loading: false, error: null })
      } catch (e) {
        if (cancelled) return
        console.error('[useResasV2] unexpected:', e && e.message ? e.message : e)
        setState({
          resasByLogement: EMPTY_MAP,
          loading: false,
          error: e && e.message ? e.message : String(e),
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, lidsKey, refetchTick])

  const invalidate = useCallback(() => {
    setRefetchTick((t) => t + 1)
  }, [])

  return { ...state, invalidate }
}
